import { useMemo } from 'react'
import type { 
  Engagement,
  EngagementSearchResponse,
  EngagementResponse,
  EngagementCollectionResponse,
  TransactionHeaderSummary,
  LookupCategory,
  LookupValue,
  Operation,
  EngagementFieldMetadata,
  CompleteEngagementRequest
} from '@/types/api/engagement'
import type { EngagementApiClient } from '@/types/api/engagement-api-client'
import { EngagementApiError } from '@/types/api/engagement-api-client'
import { useApi, statusOf } from '@/lib/api'

/**
 * Hook that provides an EngagementApiClient implementation
 * Uses sophisticated token management from api.ts internally
 * Maintains the original Swagger interface for compatibility
 */
export function useEngagementApi(): EngagementApiClient {
  const { engagementApi } = useApi()

  return useMemo(() => ({
    /**
     * Searches engagements using Azure AI Search with raw OData query parameters
     * Maps directly to GET /api/Engagement with OData query string
     * High-performance search endpoint as per swagger specification
     */
    async searchEngagements(
      search?: string,
      filter?: string,
      orderby?: string,
      top?: number,
      skip?: number,
      select?: string
    ): Promise<EngagementSearchResponse> {
      // Build query parameters
      const queryParams = new URLSearchParams()
      
      if (search !== undefined) {
        queryParams.set('$search', search)
      }
      
      if (filter !== undefined) {
        queryParams.set('$filter', filter)
      }
      
      if (orderby !== undefined) {
        queryParams.set('$orderby', orderby)
      }
      
      if (top !== undefined) {
        queryParams.set('$top', Math.min(top, 1000).toString()) // Max 1000 per swagger
      }
      
      if (skip !== undefined) {
        queryParams.set('$skip', skip.toString())
      }
      
      if (select !== undefined) {
        queryParams.set('$select', select)
      }

      const path = `/api/Engagement?${queryParams.toString()}`

      try {
        const response = await engagementApi.get<EngagementSearchResponse>(path)
        return response
      } catch (error) {
        console.error('Failed to search engagements:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Gets all versions of an engagement by business ID
     * Internal management - returns all versions
     */
    async getEngagementVersions(recordId: number): Promise<EngagementCollectionResponse> {
      const path = `/api/Engagement/${recordId}/versions`

      try {
        const response = await engagementApi.get<EngagementCollectionResponse>(path)
        return response
      } catch (error) {
        console.error('Failed to get engagement versions:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Gets a specific version of an engagement
     */
    async getEngagementVersion(recordId: number, version: number): Promise<EngagementResponse> {
      const path = `/api/Engagement/${recordId}/versions/${version}`

      try {
        const response = await engagementApi.get<EngagementResponse>(path)
        return response
      } catch (error) {
        console.error('Failed to get engagement version:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Creates a new engagement with default values
     * Auto-generates recordId and version 1, creates with "Draft" status
     * Preferred method for progressive engagement building via PATCH operations
     */
    async createEngagementWithDefaults(): Promise<EngagementResponse> {
      const path = '/api/Engagement'

      try {
        const response = await engagementApi.post<EngagementResponse>(path, {})
        return response
      } catch (error) {
        console.error('Failed to create engagement with defaults:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Creates a new engagement with provided data
     * RecordId will be auto-assigned, version starts at 1
     * RecordId should NOT be set in the request
     */
    async createEngagementWithData(engagement: Partial<Omit<Engagement, 'recordId' | 'version' | 'id' | 'createdDateTime' | 'lastModifiedDateTime' | 'createdByProfileId' | 'lastModifiedByProfileId'>>): Promise<EngagementResponse> {
      const path = '/api/Engagement/with-data'

      try {
        const response = await engagementApi.post<EngagementResponse>(path, engagement)
        return response
      } catch (error) {
        console.error('Failed to create engagement with data:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Updates an engagement using JSON Patch operations
     * System-managed fields cannot be modified
     */
    async updateEngagement(recordId: number, version: number, operations: Operation[]): Promise<EngagementResponse> {
      throw new EngagementApiError('updateEngagement not implemented', 501)
    },

    /**
     * Saves changes to an engagement (state-aware save)
     * Only Draft engagements can be saved
     */
    async saveEngagement(recordId: number, version: number, operations: Operation[]): Promise<EngagementResponse> {
      const path = `/api/Engagement/${recordId}/versions/${version}/save`

      try {
        const response = await engagementApi.patch<EngagementResponse>(path, operations)
        return response
      } catch (error) {
        console.error('Failed to save engagement:', error)
        if (error instanceof Error) {
          // The status travels with the error where there is one. Stamping 500 on everything made a
          // save the server refused, and told us why, indistinguishable from a service falling over.
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Deletes a specific version of an engagement
     * This operation cannot be undone
     */
    async deleteEngagement(recordId: number, version: number): Promise<void> {
      throw new EngagementApiError('deleteEngagement not implemented', 501)
    },

    /**
     * Creates a new version (change request) of an existing engagement
     * Copies the active engagement and sets it as a change request
     * New version created with "Draft" status and IsChangeRequest = true
     * Only one pending change request allowed per engagement at a time
     * Change requests can only be created from Active engagements
     */
    async createChangeRequest(recordId: number, comment: string): Promise<EngagementResponse> {
      const path = `/api/Engagement/${recordId}/versions`

      try {
        const response = await engagementApi.post<EngagementResponse>(path, { comment })
        return response
      } catch (error) {
        console.error('Failed to create change request:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Submits a draft engagement for review
     * Changes status from "Draft" to "PendingReview"
     * Only draft engagements can be submitted for review
     */
    async submitEngagement(recordId: number, version: number): Promise<EngagementResponse> {
      const path = `/api/Engagement/${recordId}/versions/${version}/submit`

      try {
        const response = await engagementApi.post<EngagementResponse>(path, {})
        return response
      } catch (error) {
        console.error('Failed to submit engagement:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Approves a pending engagement
     * Changes status from "PendingReview" to "Active"
     * Only engagements with PendingReview status can be approved
     */
    async approveEngagement(recordId: number, version: number): Promise<EngagementResponse> {
      const path = `/api/Engagement/${recordId}/versions/${version}/approve`

      try {
        const response = await engagementApi.post<EngagementResponse>(path, {})
        return response
      } catch (error) {
        console.error('Failed to approve engagement:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Declines a pending engagement
     * Changes status from "PendingReview" to "Draft"
     * Only engagements with PendingReview status can be declined
     */
    async declineEngagement(recordId: number, version: number): Promise<EngagementResponse> {
      const path = `/api/Engagement/${recordId}/versions/${version}/decline`

      try {
        const response = await engagementApi.post<EngagementResponse>(path, {})
        return response
      } catch (error) {
        console.error('Failed to decline engagement:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Discards a draft engagement or change request
     * If this is a change request being discarded, the base engagement reverts from PendingChange back to Active
     * Only draft engagements or change requests can be discarded
     */
    async discardEngagement(recordId: number, version: number): Promise<EngagementResponse> {
      const path = `/api/Engagement/${recordId}/versions/${version}/discard`

      try {
        const response = await engagementApi.post<EngagementResponse>(path, {})
        return response
      } catch (error) {
        console.error('Failed to discard engagement:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Generates a transaction for billing/invoicing a specific milestone
     * Only available for engagements in Active, PendingChange, Complete, Cancelled, or Replaced status
     * This action does not change the engagement status
     * Requires Finance or SystemAdministrator role permissions
     */
    async generateMilestoneTransaction(recordId: number, version: number, milestoneReference: string): Promise<TransactionHeaderSummary> {
      const path = `/api/Engagement/${recordId}/versions/${version}/milestones/${encodeURIComponent(milestoneReference)}/transactions`

      try {
        const response = await engagementApi.post<TransactionHeaderSummary>(path, {})
        return response
      } catch (error) {
        console.error('Failed to generate milestone transaction:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Gets existing milestone transactions for a specific milestone
     * Returns all existing transactions for the specified milestone.
     * Only available for engagements in Active, PendingChange, Complete, Cancelled, or Replaced status
     * Requires Finance or SystemAdministrator role permissions
     */
    async getMilestoneTransactions(recordId: number, version: number, milestoneReference: string): Promise<TransactionHeaderSummary[]> {
      const path = `/api/Engagement/${recordId}/versions/${version}/milestones/${encodeURIComponent(milestoneReference)}/transactions`

      try {
        const response = await engagementApi.get<TransactionHeaderSummary[]>(path)
        return response || []
      } catch (error) {
        console.error('Failed to get milestone transactions:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Gets all available lookup categories and their values
     */
    async getAllLookups(): Promise<LookupCategory[]> {
      const path = '/api/Lookups'

      try {
        const response = await engagementApi.get<LookupCategory[]>(path)
        return response || []
      } catch (error) {
        console.error('Failed to get all lookups:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Gets lookup values for a specific category
     */
    async getLookupsByCategory(categoryName: string): Promise<LookupValue[]> {
      const path = `/api/Lookups/${encodeURIComponent(categoryName)}`

      try {
        const response = await engagementApi.get<LookupValue[]>(path)
        return response || []
      } catch (error) {
        console.error(`Failed to get lookups for category ${categoryName}:`, error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Gets the active version of an engagement by business ID
     * External domain consumption - always returns "active" version
     */
    async getActiveEngagement(recordId: number): Promise<EngagementResponse> {
      throw new EngagementApiError('getActiveEngagement not implemented', 501)
    },

    /**
     * Gets field metadata for the Engagement model
     * Used for audit history formatting to determine field types and formats
     */
    async getFieldMetadata(): Promise<EngagementFieldMetadata> {
      const path = '/api/Metadata/engagement/fields'

      try {
        const response = await engagementApi.get<EngagementFieldMetadata>(path)
        return response || {}
      } catch (error) {
        console.error('Failed to get field metadata:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Submits an engagement for review
     */
    async submitForReview(recordId: number, version: number): Promise<EngagementResponse> {
      const path = `/api/Engagement/${recordId}/versions/${version}/submit-for-review`

      try {
        const response = await engagementApi.post<EngagementResponse>(path, {})
        return response
      } catch (error) {
        console.error('Failed to submit for review:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Recalls an engagement from review
     */
    async recallEngagement(recordId: number, version: number): Promise<EngagementResponse> {
      const path = `/api/Engagement/${recordId}/versions/${version}/recall`

      try {
        const response = await engagementApi.post<EngagementResponse>(path, {})
        return response
      } catch (error) {
        console.error('Failed to recall engagement:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Recalls an engagement to accounting
     */
    async recallToAccounting(recordId: number, version: number): Promise<EngagementResponse> {
      const path = `/api/Engagement/${recordId}/versions/${version}/recall-to-accounting`

      try {
        const response = await engagementApi.post<EngagementResponse>(path, {})
        return response
      } catch (error) {
        console.error('Failed to recall to accounting:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Marks an engagement as complete
     */
    async completeEngagement(recordId: number, version: number, request: CompleteEngagementRequest): Promise<EngagementResponse> {
      const path = `/api/Engagement/${recordId}/versions/${version}/complete`

      try {
        const response = await engagementApi.post<EngagementResponse>(path, request)
        return response
      } catch (error) {
        console.error('Failed to complete engagement:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    },

    /**
     * Reactivates an engagement
     */
    async reactivateEngagement(recordId: number, version: number): Promise<EngagementResponse> {
      const path = `/api/Engagement/${recordId}/versions/${version}/reactivate`

      try {
        const response = await engagementApi.post<EngagementResponse>(path, {})
        return response
      } catch (error) {
        console.error('Failed to reactivate engagement:', error)
        if (error instanceof Error) {
          throw new EngagementApiError(error.message, statusOf(error), error)
        }
        throw new EngagementApiError('Unknown error occurred', 500, error)
      }
    }
  }), [engagementApi])
}

// Export the error class for external use
export { EngagementApiError } 